import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PulsBar from "@/components/PulsBar";
import HomeIntroLine from "@/components/HomeIntroLine";
import SessionExpiredModal from "@/components/SessionExpiredModal";
import { installModAxiosInterceptors } from "@/utils/modAxios";
import { initAnalytics } from "@/lib/analytics";
import Home from "@/pages/Home";
import Anslagstavla from "@/pages/Anslagstavla";
import AnslagCategory from "@/pages/AnslagCategory";
import AnslagDetail from "@/pages/AnslagDetail";
import SkickaAnslag from "@/pages/SkickaAnslag";
import SkickaEvenemang from "@/pages/SkickaEvenemang";
import VeckansHandelser from "@/pages/VeckansHandelser";
import GpxVerktyg from "@/pages/GpxVerktyg";
import ModEventSourcesEnhanced from "@/pages/ModEventSourcesEnhanced";
import TipsaSida from "@/pages/TipsaSida";
import ModFlode from "@/pages/ModFlode";
import ModMediaMirror from "@/pages/ModMediaMirror";
import ModPropertyWatch from "@/pages/ModPropertyWatch";
import ModBygdepuls from "@/pages/ModBygdepuls";
import NyIBygden from "@/pages/NyIBygden";
import ForeningarEvenemang from "@/pages/ForeningarEvenemang";
import OmOss from "@/pages/OmOss";
import Riktlinjer from "@/pages/Riktlinjer";

import ModLogin from "@/pages/ModLogin";
import ModDashboard from "@/pages/ModDashboard";
import ModReview from "@/pages/ModReview";
import ModCreate from "@/pages/ModCreate";
import ModCreateEvent from "@/pages/ModCreateEvent";
import ModForeningar from "@/pages/ModForeningar";
import ModCreateForening from "@/pages/ModCreateForening";
import EvenemangDetail from "@/pages/EvenemangDetail";
import EvenemangSida from "@/pages/EvenemangSida";
import PraktisktIBygden from "@/pages/PraktisktIBygden";
import ModPraktiskt from "@/pages/ModPraktiskt";
import ModCreateVardagskort from "@/pages/ModCreateVardagskort";
import ModCreateTjanstekort from "@/pages/ModCreateTjanstekort";
import ModCreatePlats from "@/pages/ModCreatePlats";
import BilderFranBygden from "@/pages/BilderFranBygden";
import SkickaBild from "@/pages/SkickaBild";
import ForetagIBygden from "@/pages/ForetagIBygden";
import BostaderIBygden from "@/pages/BostaderIBygden";
import KartaSida from "@/pages/KartaSida";
import Kartor from "@/pages/Kartor";
import AdminPanelUnified from "@/pages/AdminPanelUnified";
import UpptackStory from "@/pages/UpptackStory";
import KronikaSida from "@/pages/KronikaSida";
import FriluftslivSida from "@/pages/FriluftslivSida";
import FriluftslivLedDetalj from "@/pages/FriluftslivLedDetalj";
import AreaOverview from "@/pages/AreaOverview";
import AccessibilityToolbar from "@/components/AccessibilityToolbar";
import CookieBanner from "@/components/CookieBanner";
import { Toaster } from "@/components/ui/sonner";
import Jamtlandssangen from "@/pages/Jamtlandssangen";
import Integritetspolicy from "@/pages/legal/Integritetspolicy";
import Anvandarvillkor from "@/pages/legal/Anvandarvillkor";
import Cookiepolicy from "@/pages/legal/Cookiepolicy";
import AnsvarigUtgivare from "@/pages/legal/AnsvarigUtgivare";
import VillkorInskickat from "@/pages/legal/VillkorInskickat";
import KontaktSida from "@/pages/KontaktSida";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function App() {
  // Etappe 4.12 — global mod-token-expiry-fångare + draft-skydd.
  useEffect(() => { installModAxiosInterceptors(); }, []);
  // Build 96.25 — initiera PostHog en gång vid app-start (om nyckel finns).
  // capture_pageview: "history_change" sköter pageviews automatiskt vid React-Router-nav.
  useEffect(() => { initAnalytics(); }, []);
  return (
    <BrowserRouter>
      <ScrollToTop />
      <div className="min-h-screen bg-bone flex flex-col font-body">
        <Header />
        <HomeIntroLine />
        <PulsBar />
        <main className="flex-1">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/anslagstavla" element={<Anslagstavla />} />
            <Route path="/anslagstavla/:slug" element={<AnslagCategory />} />
            <Route path="/anslag/:id" element={<AnslagDetail />} />
            <Route path="/skicka-anslag" element={<SkickaAnslag />} />
            <Route path="/skicka-evenemang" element={<SkickaEvenemang />} />
            <Route path="/tipsa" element={<TipsaSida />} />
            <Route path="/veckans-handelser" element={<VeckansHandelser />} />
            <Route path="/mod/gpx" element={<GpxVerktyg />} />
            <Route path="/mod/event-sources" element={<ModEventSourcesEnhanced />} />
            <Route path="/mod/flode" element={<ModFlode />} />
            <Route path="/mod/media-mirror" element={<ModMediaMirror />} />
            <Route path="/mod/property-watch" element={<ModPropertyWatch />} />
            <Route path="/mod/bygdepuls" element={<ModBygdepuls />} />
            <Route path="/ny-i-bygden" element={<NyIBygden />} />
            <Route path="/foreningar-evenemang" element={<ForeningarEvenemang />} />
            <Route path="/praktiskt" element={<PraktisktIBygden />} />
            <Route path="/friluftsliv" element={<FriluftslivSida />} />
            <Route path="/friluftsliv/omrade/:areaSlug" element={<AreaOverview />} />
            <Route path="/friluftsliv/led/:slug" element={<FriluftslivLedDetalj />} />
            <Route path="/om-oss" element={<OmOss />} />
            <Route path="/riktlinjer" element={<Riktlinjer />} />
            <Route path="/bilder" element={<BilderFranBygden />} />
            <Route path="/bilder/skicka" element={<SkickaBild />} />
            <Route path="/foretag" element={<ForetagIBygden />} />
            <Route path="/bostader" element={<BostaderIBygden />} />
            <Route path="/karta" element={<KartaSida />} />
            <Route path="/kartor" element={<Kartor />} />
            <Route path="/kronika" element={<KronikaSida />} />
            <Route path="/jamtlandssangen" element={<Jamtlandssangen />} />
            <Route path="/upptack/:slug" element={<UpptackStory />} />
            <Route path="/evenemang" element={<EvenemangSida />} />
            <Route path="/evenemang/:id" element={<EvenemangDetail />} />
            <Route path="/mod/login" element={<ModLogin />} />
            <Route path="/mod" element={<AdminPanelUnified />} />
            <Route path="/mod/anslag/:id" element={<ModReview />} />
            <Route path="/mod/skapa" element={<ModCreate />} />
            <Route path="/mod/skapa-evenemang" element={<ModCreateEvent />} />
            <Route path="/mod/foreningar" element={<ModForeningar />} />
            <Route path="/mod/foreningar/skapa" element={<ModCreateForening />} />
            <Route path="/mod/praktiskt" element={<ModPraktiskt />} />
            <Route path="/mod/praktiskt/skapa-vardagskort" element={<ModCreateVardagskort />} />
            <Route path="/mod/praktiskt/skapa-tjanstekort" element={<ModCreateTjanstekort />} />
            <Route path="/mod/plats/skapa" element={<ModCreatePlats />} />
            {/* Rättslig information */}
            <Route path="/integritetspolicy" element={<Integritetspolicy />} />
            <Route path="/integritet" element={<Integritetspolicy />} />{/* Etappe 4.8 — kortare URL-alias */}
            <Route path="/anvandarvillkor" element={<Anvandarvillkor />} />
            <Route path="/villkor-inskickat" element={<VillkorInskickat />} />{/* Etappe 4.8 — specifika villkor för inskickat material */}
            <Route path="/cookiepolicy" element={<Cookiepolicy />} />
            <Route path="/ansvarig-utgivare" element={<AnsvarigUtgivare />} />
            <Route path="/kontakt" element={<KontaktSida />} />
          </Routes>
        </main>
        <Footer />
        <AccessibilityToolbar />
        <CookieBanner />
        <SessionExpiredModal />
        <Toaster position="top-center" richColors closeButton />
      </div>
    </BrowserRouter>
  );
}

export default App;
